import {
  Component,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  ViewChildren,
  ChangeDetectorRef,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { MatCheckbox } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { ReserveHealthService } from '../../shared/services/reserve-health.service';
import {
  DiseaseOption,
  QuestionnaireForm,
  Questions,
} from '../../shared/interfaces/reserve-health.model';
import { MatError, MatInput } from '@angular/material/input';
import { PersonalHistoryDiseasePipe } from '../../pipes/personal-history-disease.pipe';
import { MatButton } from '@angular/material/button';
import { Router } from '@angular/router';
import { FamilyHistoryDiseasePipe } from '../../pipes/family-history-disease.pipe';
import { SubjectiveSymptomDiseasePipe } from '../../pipes/subjective-symptom-disease.pipe';
import { MatDialog } from '@angular/material/dialog';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import {
  HEALTH_QUESTIONNAIRE_FIELDS,
  NONE_OF_FOLLOWING,
} from '../../shared/constants/health-questionnaire-fields.constants';
import { API_PARAMS } from '../../shared/constants/api.constants';
import { AuthService } from '../../shared/services/auth.service';
import { Location, NgClass, ViewportScroller } from '@angular/common';
import { v4 as uuidv4 } from 'uuid';
import { STORAGE_KEY } from '../../shared/constants/store-key.constants';

/**
 * 填寫健康背景(個人/家族/自覺症狀)
 */
@Component({
  selector: 'app-reserve-health',
  standalone: true,
  imports: [
    TranslatePipe,
    MatCheckbox,
    FormsModule,
    MatInput,
    PersonalHistoryDiseasePipe,
    MatButton,
    FamilyHistoryDiseasePipe,
    SubjectiveSymptomDiseasePipe,
    MatError,
    NgClass,
  ],
  templateUrl: './reserve-health.component.html',
  styleUrl: './reserve-health.component.scss',
})
export class ReserveHealthComponent implements OnInit {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  reserveHealthService: ReserveHealthService = inject(ReserveHealthService);
  translateService: TranslateService = inject(TranslateService);
  authService: AuthService = inject(AuthService);
  dialog: MatDialog = inject(MatDialog);
  router: Router = inject(Router);
  private location = inject(Location);
  private cdr = inject(ChangeDetectorRef);

  personalHistoryDiseasePipe: PersonalHistoryDiseasePipe =
    new PersonalHistoryDiseasePipe();
  familyHistoryDiseasePipe: FamilyHistoryDiseasePipe =
    new FamilyHistoryDiseasePipe();
  subjectiveSymptomDiseasePipe: SubjectiveSymptomDiseasePipe =
    new SubjectiveSymptomDiseasePipe();

  /**
   * 當前步驟
   * 1: 個人病史
   * 2: 家族病史
   * 3: 自覺症狀
   */
  step: number = 1;

  showError: boolean = false; // 是否顯示錯誤訊息

  personalNoneOfFollowing: boolean = false; // 個人病史"以下皆無"是否勾選
  familyNoneOfFollowing: boolean = false; // 個人病史"以下皆無"是否勾選
  subjectiveSymptomNoneOfFollowing: boolean = false; // 個人病史"以下皆無"是否勾選

  personalDiseaseDetails: { [code: string]: string } = {}; // 個人病史詳細症狀，使用者對各特別項目填寫的答案。 在階段提交後會將 personalDiseaseDetails的詳細資料轉換並存入 personalDiseases
  otherPersonalDisease?: string; // 個人病史其他症狀
  otherFamilyDisease?: string; // 家族病史其他症狀
  otherSubjectiveSymptomDisease?: string; // 自覺其他症狀

  // 個人病史欄位 用來架構前端表單的參數
  personalQuestionnaire?: QuestionnaireForm;
  familyQuestionnaire?: QuestionnaireForm;
  subjectiveSymptomQuestionnaire?: QuestionnaireForm;

  // 有勾的都會放到陣列傳給下一階段呼叫CB-046查詢個人方案結果 陣列中是中文串接的格式
  personalDiseases: Array<string> = []; // 勾選之"個人病史"欄位 交由下一階段個人方案結果頁面中 CB-046 來傳送API請求
  familyDiseases: Array<string> = []; // 勾選之"家族病史"欄位
  subjectiveSymptomDiseases: Array<string> = []; // 勾選之"自覺症狀"欄位

  // 下列三個參數皆為CB-043 取得HIS見調表紀錄所得，用於設定初始勾選項並後續傳給CB-056送出完整資料API做使用
  personalDiseaseCheck: Questions = { questionId: '474', questionAnswers: [] };
  familyDiseaseCheck: Questions = { questionId: '483', questionAnswers: [] };
  subjectiveSymptomDiseaseCheck: Questions = {
    questionId: '472',
    questionAnswers: [],
  };

  @ViewChildren('fieldRef') fieldRefs!: QueryList<ElementRef>;

  constructor(private viewportScroller: ViewportScroller) {}

  ngOnInit() {
    // 恢復當前步驟
    const savedStep = this.sharedStorageService.get('currentHealthStep');
    if (savedStep) {
      console.log('恢復當前步驟:', savedStep);
      this.step = savedStep;
    }
    // 取得暫存的所有資料
    const tempDetails = this.sharedStorageService.get(STORAGE_KEY.TEMP_PERSONAL_DISEASE_DETAILS);
    const tempCheck = this.sharedStorageService.get(STORAGE_KEY.TEMP_PERSONAL_DISEASE_CHECK);
    const tempNoneOfFollowing = this.sharedStorageService.get(STORAGE_KEY.TEMP_PERSONAL_NONE_OF_FOLLOWING);
    const tempFamilyCheck = this.sharedStorageService.get(STORAGE_KEY.TEMP_FAMILY_DISEASE_CHECK);
    const tempFamilyNone = this.sharedStorageService.get(STORAGE_KEY.TEMP_FAMILY_NONE_OF_FOLLOWING);
    const tempSubjectiveCheck = this.sharedStorageService.get(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_DISEASE_CHECK);
    const tempSubjectiveNone = this.sharedStorageService.get(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_NONE_OF_FOLLOWING);
    
    // 判斷是否有暫存資料
    const hasTempData = tempDetails || tempCheck || (tempNoneOfFollowing !== null && tempNoneOfFollowing !== undefined) ||
                        tempFamilyCheck || (tempFamilyNone !== null && tempFamilyNone !== undefined) ||
                        tempSubjectiveCheck || (tempSubjectiveNone !== null && tempSubjectiveNone !== undefined);
    
    if (tempDetails) {
      this.personalDiseaseDetails = tempDetails;
    }
    if (tempCheck) {
      this.personalDiseaseCheck = tempCheck;
      // 恢復勾選狀態到 personalDiseases
      this.personalDiseases = this.personalDiseaseCheck.questionAnswers
        .filter(ans => {
          return ans.choiceId !== '1540' && // 排除"以下皆無"
                ans.choiceId !== '1568' && // 排除骨折詳細說明ID
                ans.choiceId !== '1569';   // 排除癌症詳細說明ID
        })
        .map(ans => {
          const option = HEALTH_QUESTIONNAIRE_FIELDS.PERSONAL_HISTORY.diseaseGroups
            .flatMap(group => group.diseaseOptions)
            .find(opt => opt.choiceId === ans.choiceId);
          return option ? this.personalHistoryDiseasePipe.transform(option.name) : '';
        })
        .filter(name => name !== '');
      }
    if (tempNoneOfFollowing !== null && tempNoneOfFollowing !== undefined) {
      this.personalNoneOfFollowing = tempNoneOfFollowing;
    }
    // 家族病史
    if (tempFamilyCheck) {
    this.familyDiseaseCheck = {
      ...tempFamilyCheck,
      questionAnswers: tempFamilyCheck.questionAnswers.filter(ans => ans && ans.choiceId)
    };
    
    console.log('準備恢復家族病史:', this.familyDiseaseCheck.questionAnswers);
    
    this.familyDiseases = this.familyDiseaseCheck.questionAnswers
      .filter(ans => ans.choiceId !== '1592')
      .map(ans => {
        const option = HEALTH_QUESTIONNAIRE_FIELDS.FAMILY_HISTORY.diseaseGroups
          .flatMap(group => group.diseaseOptions)
          .find(opt => opt.choiceId === ans.choiceId);
        
        console.log(`查找 choiceId ${ans.choiceId}:`, option); // 👈 加這行
        
        return option ? this.familyHistoryDiseasePipe.transform(option.name) : '';
      })
      .filter(name => name !== '');
      
    console.log('恢復後的 familyDiseases:', this.familyDiseases);
  }
    if (tempFamilyNone !== null && tempFamilyNone !== undefined) {
      this.familyNoneOfFollowing = tempFamilyNone;
    }
      // 自覺症狀
      if (tempSubjectiveCheck) {
      this.subjectiveSymptomDiseaseCheck = {
        ...tempSubjectiveCheck,
        questionAnswers: tempSubjectiveCheck.questionAnswers.filter(ans => ans && ans.choiceId)
      };

      console.log('準備恢復自覺症狀:', this.subjectiveSymptomDiseaseCheck.questionAnswers);

      this.subjectiveSymptomDiseases = this.subjectiveSymptomDiseaseCheck.questionAnswers
        .filter(ans => ans.choiceId !== '1510')
        .map(ans => {
          const option = HEALTH_QUESTIONNAIRE_FIELDS.SUBJECTIVE_SYMPTOMS.diseaseGroups
            .flatMap(group => group.diseaseOptions)
            .find(opt => opt.choiceId === ans.choiceId);

            console.log(`查找 choiceId ${ans.choiceId}:`, option);

          return option ? this.subjectiveSymptomDiseasePipe.transform(option.name) : '';
        })
        .filter(name => name !== '');

        console.log('恢復後的 subjectiveSymptomDiseases:', this.subjectiveSymptomDiseases);
    }

    if (tempSubjectiveNone !== null && tempSubjectiveNone !== undefined) {
      this.subjectiveSymptomNoneOfFollowing = tempSubjectiveNone;
    }

    if (hasTempData) {
      setTimeout(() => {
        this.cdr.detectChanges();
      });
    }

    // 將當前預約步驟設置為2
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 2);
    });

    // 因快速健檢不會填寫真正ID 為此創立UUID暫時滿足API身分需求
    const userPlanRequest = this.sharedStorageService.get('userPlanRequest');

    if (userPlanRequest) {
      userPlanRequest.funcType = '0';
      if (this.sharedStorageService.get('loginStatus') === 'newFriend' && this.sharedStorageService.get('reserveType') === '1') {
        userPlanRequest.identityType = '1';
        userPlanRequest.idNo = uuidv4();
        userPlanRequest.uuid = userPlanRequest.idNo;
      } else if (this.sharedStorageService.get('loginStatus') === 'newFriend' && this.sharedStorageService.get('reserveType') === '2') {
        userPlanRequest.identityType = '0';
        userPlanRequest.idNo = this.sharedStorageService.get('idLoginRequest')?.idNo ?? '';
        userPlanRequest.uuid = '';
      } 
      else {
        userPlanRequest.identityType = '0';
        
        const createMemberRequest = this.sharedStorageService.get(STORAGE_KEY.CREATE_MEMBER_REQUEST) as { idNo?: string } | undefined;
        
        if (createMemberRequest?.idNo) {
          userPlanRequest.idNo = createMemberRequest.idNo;
        } else {
          // 從 idLoginRequest 取得身分證字號
          const idLoginRequest = this.sharedStorageService.get('idLoginRequest' as never) as { idNo?: string } | undefined;
          if (idLoginRequest?.idNo) {
            userPlanRequest.idNo = idLoginRequest.idNo;
          }
        }
      }

      // 只有在沒有暫存資料時才從 API 載入
      if (!hasTempData) {
        console.log('送出的 userPlanRequest:', userPlanRequest);
        this.reserveHealthService
          .getQuestionnaireField(userPlanRequest)
          .subscribe(res => {
            console.log('完整 API 回傳:', JSON.stringify(res, null, 2));
            console.log('API 回傳的完整資料:', res);
            if (res.statusCode === 'S0000' && res.data?.questions) {
              // 處理個人病史 (questionId: 474)
              this.personalDiseaseCheck = res.data.questions.find(
                question => question.questionId === '474'
              ) ?? { questionId: '474', questionAnswers: [] };
              
              this.personalDiseaseCheck.questionAnswers.forEach(disease => {
                HEALTH_QUESTIONNAIRE_FIELDS.PERSONAL_HISTORY.diseaseGroups
                  .flatMap(group => group.diseaseOptions)
                  .forEach(option => {
                    if (disease.choiceId === '1540') {
                      this.personalNoneOfFollowing = true;
                      this.togglePersonalNoneOfFollowing();
                    } else if (disease.choiceId === option.choiceId) {
                      this.onPersonalDiseaseCheckboxChange(true, option);
                    }
                  });
              });

              // 處理骨折詳細說明 (questionId: 475)
              const fractureDetail = res.data.questions.find(
                question => question.questionId === '475'
              );
              if (fractureDetail?.questionAnswers?.[0]) {
                this.personalDiseaseDetails['1564'] = fractureDetail.questionAnswers[0].answer;
                // 同時將詳細說明加入 personalDiseaseCheck
                if (!this.personalDiseaseCheck.questionAnswers.some(ans => ans.choiceId === '1568')) {
                  this.personalDiseaseCheck.questionAnswers.push({
                    choiceId: '1568',
                    answer: fractureDetail.questionAnswers[0].answer
                  });
                }
              }

              // 處理癌症詳細說明 (questionId: 476)
              const cancerDetail = res.data.questions.find(
                question => question.questionId === '476'
              );
              if (cancerDetail?.questionAnswers?.[0]) {
                this.personalDiseaseDetails['1565'] = cancerDetail.questionAnswers[0].answer;
                // 同時將詳細說明加入 personalDiseaseCheck
                if (!this.personalDiseaseCheck.questionAnswers.some(ans => ans.choiceId === '1569')) {
                  this.personalDiseaseCheck.questionAnswers.push({
                    choiceId: '1569',
                    answer: cancerDetail.questionAnswers[0].answer
                  });
                }
              }

              this.familyDiseaseCheck = res.data.questions.find(
                question => question.questionId === '483'
              ) ?? { questionId: '483', questionAnswers: [] };
              this.familyDiseaseCheck.questionAnswers.forEach(disease => {
                HEALTH_QUESTIONNAIRE_FIELDS.FAMILY_HISTORY.diseaseGroups
                  .flatMap(group => group.diseaseOptions)
                  .forEach(option => {
                    if (disease.choiceId === '1592') {
                      this.familyNoneOfFollowing = true;
                      this.toggleFamilyNoneOfFollowing();
                    } else if (disease.choiceId === option.choiceId) {
                      this.onFamilyDiseaseChange(true, option);
                    }
                  });
              });

              this.subjectiveSymptomDiseaseCheck = res.data.questions.find(
                question => question.questionId === '472'
              ) ?? { questionId: '472', questionAnswers: [] };
              this.subjectiveSymptomDiseaseCheck.questionAnswers.forEach(
                disease => {
                  HEALTH_QUESTIONNAIRE_FIELDS.SUBJECTIVE_SYMPTOMS.diseaseGroups
                    .flatMap(group => group.diseaseOptions)
                    .forEach(option => {
                      if (disease.choiceId === '1510') {
                        this.subjectiveSymptomNoneOfFollowing = true;
                        this.toggleSubjectiveSymptomNoneOfFollowing();
                      } else if (disease.choiceId === option.choiceId) {
                        this.onSubjectiveSymptomDiseaseChange(true, option);
                      }
                    });
                }
              );
            }
          });
      }
    }

    this.personalQuestionnaire = HEALTH_QUESTIONNAIRE_FIELDS.PERSONAL_HISTORY;
    this.familyQuestionnaire = HEALTH_QUESTIONNAIRE_FIELDS.FAMILY_HISTORY;
    this.subjectiveSymptomQuestionnaire =
      HEALTH_QUESTIONNAIRE_FIELDS.SUBJECTIVE_SYMPTOMS;
  }

  // 切換個人病史"以下皆無"選項
  togglePersonalNoneOfFollowing() {
    // 如果勾選以下皆無
    if (this.personalNoneOfFollowing) {
      const value =
        this.personalHistoryDiseasePipe.transform(NONE_OF_FOLLOWING);
      this.personalDiseases = [value]; // 清空個人病史勾選 只放 "既往病史_以下皆無"
      this.personalDiseaseDetails = {}; // 清空個人病史詳細症狀
      this.otherPersonalDisease = ''; // 清空其他症狀輸入

      // 如果勾選以下皆無 陣列終將只會有此ID代表無個人病史
      if (
        !this.personalDiseaseCheck.questionAnswers.some(
          asw => asw.choiceId === '1540'
        )
      ) {
        this.personalDiseaseCheck.questionAnswers = [
          { choiceId: '1540', answer: '1' },
        ];
      }
    } else {
      // 如果取消勾選 清空陣列 後續勾選其他項目會再放入
      this.personalDiseases = [];
      this.personalDiseaseCheck.questionAnswers = [];
    }
  }

  // 切換家族病史"以下皆無"選項
  toggleFamilyNoneOfFollowing() {
    if (this.familyNoneOfFollowing) {
      const value = this.familyHistoryDiseasePipe.transform(NONE_OF_FOLLOWING);
      this.familyDiseases = [value]; // 清空家族病史勾選 只放 "家族病史_以下皆無"
      this.otherFamilyDisease = ''; // 清空其他症狀輸入

      // 如果勾選以下皆無 陣列終將只會有此ID代表無家族病史
      if (
        !this.familyDiseaseCheck.questionAnswers.some(
          asw => asw.choiceId === '1592'
        )
      ) {
        this.familyDiseaseCheck.questionAnswers = [
          { choiceId: '1592', answer: '1' },
        ];
      }
    } else {
      // 如果取消勾選 清空陣列 後續勾選其他項目會再放入
      this.familyDiseases = [];
      this.familyDiseaseCheck.questionAnswers = [];
    }
  }

  // 切換自覺症狀"以下皆無"選項
  toggleSubjectiveSymptomNoneOfFollowing() {
    if (this.subjectiveSymptomNoneOfFollowing) {
      const value =
        this.subjectiveSymptomDiseasePipe.transform(NONE_OF_FOLLOWING);
      this.subjectiveSymptomDiseases = [value]; // 清空自覺症狀勾選 只放 "自覺症狀_以下皆無"
      this.otherSubjectiveSymptomDisease = ''; // 清空其他症狀輸入

      // 如果勾選以下皆無 陣列終將只會有此ID代表無自覺症狀
      if (
        !this.subjectiveSymptomDiseaseCheck.questionAnswers.some(
          asw => asw.choiceId === '1510'
        )
      ) {
        this.subjectiveSymptomDiseaseCheck.questionAnswers = [
          { choiceId: '1510', answer: '1' },
        ];
      }
    } else {
      // 如果取消勾選 清空陣列 後續勾選其他項目會再放入
      this.subjectiveSymptomDiseases = [];
      this.subjectiveSymptomDiseaseCheck.questionAnswers = [];
    }
  }

  // 勾選/取消個人病史症狀
  onPersonalDiseaseCheckboxChange(
    checked: boolean,
    diseaseOption: DiseaseOption
  ) {
    const value = this.personalHistoryDiseasePipe.transform(diseaseOption.name);
    if (diseaseOption.detailPlaceholder) {
      // 如果此欄位是詳細症狀 勾選後續需將使用者填寫的詳細症狀存入 personalDiseaseDetails (由 onPersonalDiseaseDetailInput 函式處理)
      if (checked) {
        this.personalDiseases.push(value);

        if (!this.personalDiseaseDetails[diseaseOption.choiceId]) {
          this.personalDiseaseDetails[diseaseOption.choiceId] = '';
        }

        // 而在personalDiseaseCheck 除了原本勾選的ID之外，需另將與勾選欄位對應之 詳細說明欄位的ID及答案存入
        if (
          !this.personalDiseaseCheck.questionAnswers.some(
            ans => ans.choiceId === diseaseOption.choiceId
          )
        ) {
          this.personalDiseaseCheck.questionAnswers.push({
            choiceId: diseaseOption.choiceId,
            answer: diseaseOption.answer,
          });
          this.personalDiseaseCheck.questionAnswers.push({
            choiceId: diseaseOption.detailPlaceholderId!,
            answer: this.personalDiseaseDetails[diseaseOption.choiceId],
          });
        }
      } else {
        // 取消勾選，在兩者中移除上述欄位
        delete this.personalDiseaseDetails[diseaseOption.choiceId];

        const idx = this.personalDiseases.indexOf(value);
        if (idx > -1) this.personalDiseases.splice(idx, 1);

        this.personalDiseaseCheck.questionAnswers =
          this.personalDiseaseCheck.questionAnswers.filter(ans => {
            return (
              ans.choiceId !== diseaseOption.choiceId &&
              ans.choiceId !== diseaseOption.detailPlaceholderId
            );
          });
      }
    } else {
      if (checked) {
        // 若此欄位無對應的詳細說明欄位 直接存入 personalDiseases 及 personalDiseaseCheck
        this.personalDiseases.push(value);
        if (
          !this.personalDiseaseCheck.questionAnswers.some(
            ans => ans.choiceId === diseaseOption.choiceId
          )
        ) {
          this.personalDiseaseCheck.questionAnswers.push({
            choiceId: diseaseOption.choiceId,
            answer: diseaseOption.answer,
          });
        }
      } else {
        const idx = this.personalDiseases.indexOf(value);
        if (idx > -1) this.personalDiseases.splice(idx, 1);

        this.personalDiseaseCheck.questionAnswers =
          this.personalDiseaseCheck.questionAnswers.filter(ans => {
            return ans.choiceId !== diseaseOption.choiceId;
          });
      }
    }
  }

  isComposing = false;
  onCompositionStart() {
    this.isComposing = true;
  }
  onCompositionEnd(diseaseOption: DiseaseOption) {
    this.isComposing = false;
    this.onPersonalDiseaseDetailInput(diseaseOption);
  }

  onPersonalDiseaseDetailInput(diseaseOption: DiseaseOption): void {
    const detailText =
      this.personalDiseaseDetails[diseaseOption.choiceId] ?? '';
    const detailId = diseaseOption.detailPlaceholderId!;
    const checkboxId = diseaseOption.choiceId;
    const checkboxAns = diseaseOption.answer;

    const value = this.personalHistoryDiseasePipe.transform(diseaseOption.name);
    if (!this.personalDiseases.includes(value))
      this.personalDiseases.push(value);

    const detailAnswer = this.personalDiseaseCheck.questionAnswers.find(
      ans => ans.choiceId === detailId
    );
    if (detailAnswer) {
      detailAnswer.answer = detailText;
    } else {
      this.personalDiseaseCheck.questionAnswers.push({
        choiceId: checkboxId,
        answer: checkboxAns,
      });
      this.personalDiseaseCheck.questionAnswers.push({
        choiceId: detailId,
        answer: detailText,
      });
    }

    this.personalNoneOfFollowing = false;
  }

  // 勾選/取消家族病史症狀
  onFamilyDiseaseChange(checked: boolean, diseaseOption: DiseaseOption) {
    if (!diseaseOption.choiceId) {
      console.warn('家族病史選項缺少 choiceId:', diseaseOption.name);
      return;
    }
    const value = this.familyHistoryDiseasePipe.transform(diseaseOption.name);
    if (checked) {
      this.familyDiseases.push(value);

      if (
        !this.familyDiseaseCheck.questionAnswers.some(
          ans => ans.choiceId === diseaseOption.choiceId
        )
      ) {
        this.familyDiseaseCheck.questionAnswers.push({
          choiceId: diseaseOption.choiceId,
          answer: diseaseOption.answer,
        });
      }
    } else {
      const idx = this.familyDiseases.indexOf(value);
      if (idx > -1) this.familyDiseases.splice(idx, 1);

      this.familyDiseaseCheck.questionAnswers =
        this.familyDiseaseCheck.questionAnswers.filter(ans => {
          return ans.choiceId !== diseaseOption.choiceId;
        });
    }
  }

  // 勾選/取消自覺症狀
  onSubjectiveSymptomDiseaseChange(
    checked: boolean,
    diseaseOption: DiseaseOption
  ) {
    if (!diseaseOption.choiceId) {
      console.warn('自覺症狀選項缺少 choiceId:', diseaseOption.name);
      return;
    }
    const value = this.subjectiveSymptomDiseasePipe.transform(
      diseaseOption.name
    );
    if (checked) {
      this.subjectiveSymptomDiseases.push(value);

      if (
        !this.subjectiveSymptomDiseaseCheck.questionAnswers.some(
          ans => ans.choiceId === diseaseOption.choiceId
        )
      ) {
        this.subjectiveSymptomDiseaseCheck.questionAnswers.push({
          choiceId: diseaseOption.choiceId,
          answer: diseaseOption.answer,
        });
      }
    } else {
      const idx = this.subjectiveSymptomDiseases.indexOf(value);
      if (idx > -1) this.subjectiveSymptomDiseases.splice(idx, 1);

      this.subjectiveSymptomDiseaseCheck.questionAnswers =
        this.subjectiveSymptomDiseaseCheck.questionAnswers.filter(ans => {
          return ans.choiceId !== diseaseOption.choiceId;
        });
    }
  }

  // 畫面滾動到頂部
  scrollToTop(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  // 點擊上一步按鈕
  handlePreviousClick() {
    console.log('保存前的 personalDiseaseCheck:', JSON.stringify(this.personalDiseaseCheck, null, 2));
    console.log('保存前的 personalDiseases:', this.personalDiseases);
    console.log('保存前的 familyDiseaseCheck:', JSON.stringify(this.familyDiseaseCheck, null, 2));
    console.log('保存前的 familyDiseases:', this.familyDiseases);
    console.log('保存前的 subjectiveSymptomDiseaseCheck:', JSON.stringify(this.subjectiveSymptomDiseaseCheck, null, 2));
    console.log('保存前的 subjectiveSymptomDiseases:', this.subjectiveSymptomDiseases);
  
    // 暫存目前填寫的詳細資料
    this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_DISEASE_DETAILS, this.personalDiseaseDetails);
    this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_DISEASE_CHECK, this.personalDiseaseCheck);
    this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_NONE_OF_FOLLOWING, this.personalNoneOfFollowing);
    this.sharedStorageService.set(STORAGE_KEY.TEMP_FAMILY_DISEASE_CHECK, this.familyDiseaseCheck);
    this.sharedStorageService.set(STORAGE_KEY.TEMP_FAMILY_NONE_OF_FOLLOWING, this.familyNoneOfFollowing);
    this.sharedStorageService.set(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_DISEASE_CHECK, this.subjectiveSymptomDiseaseCheck);
    this.sharedStorageService.set(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_NONE_OF_FOLLOWING, this.subjectiveSymptomNoneOfFollowing);


    if (this.step === 1) {
      this.router.navigate(['/healthcheck/reserve/personalinfo']).then();
    } else {
      this.step -= 1;
      this.sharedStorageService.set('currentHealthStep', this.step);
      this.updateUrlByStep(this.step);
      this.scrollToTop();
    }
  }

  // 點擊下一步按鈕
  handleNextClick(event: Event) {
    (event.target as HTMLElement).blur();

    if (this.step === 1) {
      // 檢查個人病史詳細症狀是否填寫
      if (
        Object.entries(this.personalDiseaseDetails).some(
          ([, value]) => value === ''
        )
      ) {
        this.scrollToFirstInvalidField();
        this.showError = true;
        return;
      }
      this.showError = false;
      
      
      // 在 step 1 切換到 step 2 時也保存
      this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_DISEASE_DETAILS, this.personalDiseaseDetails);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_DISEASE_CHECK, this.personalDiseaseCheck);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_NONE_OF_FOLLOWING, this.personalNoneOfFollowing);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_FAMILY_DISEASE_CHECK, this.familyDiseaseCheck);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_FAMILY_NONE_OF_FOLLOWING, this.familyNoneOfFollowing);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_DISEASE_CHECK, this.subjectiveSymptomDiseaseCheck);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_NONE_OF_FOLLOWING, this.subjectiveSymptomNoneOfFollowing);
    }

    // 驗證正確勾選
    if (
      (this.step === 1 &&
        !this.personalNoneOfFollowing &&
        this.personalDiseases.length === 0 &&
        Object.entries(this.personalDiseaseDetails).length === 0) ||
      (this.step === 2 &&
        !this.familyNoneOfFollowing &&
        this.familyDiseases.length === 0) ||
      (this.step === 3 &&
        !this.subjectiveSymptomNoneOfFollowing &&
        this.subjectiveSymptomDiseases.length === 0)
    ) {
      const hint = this.dialog.open(SingleActionDialogComponent, {
        disableClose: true,
        data: {
          title: this.translateService.instant(
            'reserve-health.no-field-been-checked'
          ),
          message: '',
          showCloseButton: false,
          buttonText: this.translateService.instant(
            'common.button.i-understood'
          ),
          action: () => hint.close(),
        },
      });
      return;
    }

    if (this.step === 3) {
      // 保存當前步驟
      this.sharedStorageService.set('currentHealthStep', this.step);
      
      // 確保資料已恢復
      this.ensureDataRestored();
      
      // 將勾選答案放入shared storage
      const userPlanRequest = this.sharedStorageService.get('userPlanRequest');
      const businessPlanRequest = this.sharedStorageService.get('businessPlanRequest');
      if (userPlanRequest) {
        console.log('寫入前的資料狀態:');
        console.log('personalDiseases:', this.personalDiseases);
        console.log('familyDiseases:', this.familyDiseases);
        console.log('subjectiveSymptomDiseases:', this.subjectiveSymptomDiseases);
        
        userPlanRequest.history = this.personalDiseases;
        userPlanRequest.family = this.familyDiseases;
        userPlanRequest.subjectiveSymptoms = this.subjectiveSymptomDiseases;

        // 個人病史清單內使用personalDiseaseDetails內部使用者有手動填的詳細症狀 來添加疾病詳細名稱 存入 userPlanRequest 中之 fractureAnswer 及 cancerAnswer
        Object.entries(this.personalDiseaseDetails)
          .filter(([, value]) => value && value.trim().length > 0)
          .forEach(([key, value]) => {
            console.log('準備送出的詳細資料:', key, value);
            if (key === '1564') userPlanRequest.fractureAnswer = value;
            if (key === '1565') userPlanRequest.cancerAnswer = value;
          });

        this.sharedStorageService.set('userPlanRequest', userPlanRequest);
        
        console.log('寫入後的 userPlanRequest:', userPlanRequest);
      }

      // 將三個表格中各勾選的檢查項目所產生的 questionnaireChecks 放入共享儲存中，供後面的 CB-056 請求使用
      const questionnaireChecks = [
        this.personalDiseaseCheck,
        this.familyDiseaseCheck,
        this.subjectiveSymptomDiseaseCheck,
      ];
      this.sharedStorageService.set('questionnaireChecks', questionnaireChecks);

      this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_DISEASE_DETAILS, this.personalDiseaseDetails);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_DISEASE_CHECK, this.personalDiseaseCheck);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_NONE_OF_FOLLOWING, this.personalNoneOfFollowing);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_FAMILY_DISEASE_CHECK, this.familyDiseaseCheck);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_FAMILY_NONE_OF_FOLLOWING, this.familyNoneOfFollowing);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_DISEASE_CHECK, this.subjectiveSymptomDiseaseCheck);
      this.sharedStorageService.set(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_NONE_OF_FOLLOWING, this.subjectiveSymptomNoneOfFollowing);

      // 清除所有暫存資料
      // this.sharedStorageService.remove(STORAGE_KEY.TEMP_PERSONAL_DISEASE_DETAILS);
      // this.sharedStorageService.remove(STORAGE_KEY.TEMP_PERSONAL_DISEASE_CHECK);
      // this.sharedStorageService.remove(STORAGE_KEY.TEMP_PERSONAL_NONE_OF_FOLLOWING);
      // this.sharedStorageService.remove(STORAGE_KEY.TEMP_FAMILY_DISEASE_CHECK);
      // this.sharedStorageService.remove(STORAGE_KEY.TEMP_FAMILY_NONE_OF_FOLLOWING);
      // this.sharedStorageService.remove(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_DISEASE_CHECK);
      // this.sharedStorageService.remove(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_NONE_OF_FOLLOWING);

      this.router.navigate(['/healthcheck/reserve/loading']).then();
    } else {
      // 在 step 2 切換到 step 3 時也保存
      if (this.step === 2) {
        this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_DISEASE_DETAILS, this.personalDiseaseDetails);
        this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_DISEASE_CHECK, this.personalDiseaseCheck);
        this.sharedStorageService.set(STORAGE_KEY.TEMP_PERSONAL_NONE_OF_FOLLOWING, this.personalNoneOfFollowing);
        this.sharedStorageService.set(STORAGE_KEY.TEMP_FAMILY_DISEASE_CHECK, this.familyDiseaseCheck);
        this.sharedStorageService.set(STORAGE_KEY.TEMP_FAMILY_NONE_OF_FOLLOWING, this.familyNoneOfFollowing);
        this.sharedStorageService.set(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_DISEASE_CHECK, this.subjectiveSymptomDiseaseCheck);
        this.sharedStorageService.set(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_NONE_OF_FOLLOWING, this.subjectiveSymptomNoneOfFollowing);
      }

      this.step += 1;

      this.updateUrlByStep(this.step);
      // 保存當前步驟
      this.sharedStorageService.set('currentHealthStep', this.step);
      
      this.scrollToTop();
    }
  }

  // function to scroll to the first invalid field
  scrollToFirstInvalidField() {
    const refs = this.fieldRefs.toArray();

    for (const ref of refs) {
      // get the target element with the wanted data-control-name attribute then scroll
      const choiceId = ref.nativeElement.getAttribute('data-choice-id');
      if (
        choiceId &&
        this.personalDiseaseDetails.hasOwnProperty(choiceId) &&
        this.personalDiseaseDetails[choiceId] === ''
      ) {
        ref.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        break;
      }
    }
  }

  // 確保資料已恢復
  private ensureDataRestored(): void {
    // 如果陣列為空，強制重新恢復
    if (this.personalDiseases.length === 0 || this.familyDiseases.length === 0 || this.subjectiveSymptomDiseases.length === 0) {
      // 重新執行恢復邏輯（從 ngOnInit 複製相關部分）
      this.restoreDataFromStorage();
    }
  }

  // 重新整理時，將資料重新從shared storage中載入
  private restoreDataFromStorage(): void {
    // 檢查 HEALTH_QUESTIONNAIRE_FIELDS 是否已載入
    if (!HEALTH_QUESTIONNAIRE_FIELDS?.PERSONAL_HISTORY || 
        !HEALTH_QUESTIONNAIRE_FIELDS?.FAMILY_HISTORY || 
        !HEALTH_QUESTIONNAIRE_FIELDS?.SUBJECTIVE_SYMPTOMS) {
      console.log('HEALTH_QUESTIONNAIRE_FIELDS 尚未載入完成，延遲重試...');
      setTimeout(() => {
        this.restoreDataFromStorage();
      }, 100);
      return;
    }

    // 恢復個人病史
    const tempCheck = this.sharedStorageService.get(STORAGE_KEY.TEMP_PERSONAL_DISEASE_CHECK);
    if (tempCheck) {
      this.personalDiseaseCheck = tempCheck;
      
      // 檢查是否有"以下皆無"
      const hasNoneOfFollowing = this.personalDiseaseCheck.questionAnswers.some(ans => ans.choiceId === '1540');
      if (hasNoneOfFollowing) {
        this.personalNoneOfFollowing = true;
        const value = this.personalHistoryDiseasePipe.transform(NONE_OF_FOLLOWING);
        this.personalDiseases = [value];
      } else {
        // 恢復其他選項
        this.personalDiseases = this.personalDiseaseCheck.questionAnswers
          .filter(ans => ans.choiceId !== '1568' && ans.choiceId !== '1569')
          .map(ans => {
            const option = HEALTH_QUESTIONNAIRE_FIELDS.PERSONAL_HISTORY.diseaseGroups
              .flatMap(group => group.diseaseOptions)
              .find(opt => opt.choiceId === ans.choiceId);
            return option ? this.personalHistoryDiseasePipe.transform(option.name) : '';
          })
          .filter(name => name !== '');
      }
    }

    // 恢復家族病史
    const tempFamilyCheck = this.sharedStorageService.get(STORAGE_KEY.TEMP_FAMILY_DISEASE_CHECK);
    if (tempFamilyCheck) {
      this.familyDiseaseCheck = tempFamilyCheck;
      
      // 檢查是否有"以下皆無"
      const hasNoneOfFollowing = this.familyDiseaseCheck.questionAnswers.some(ans => ans.choiceId === '1592');
      if (hasNoneOfFollowing) {
        this.familyNoneOfFollowing = true;
        const value = this.familyHistoryDiseasePipe.transform(NONE_OF_FOLLOWING);
        this.familyDiseases = [value];
      } else {
        // 恢復其他選項
        this.familyDiseases = this.familyDiseaseCheck.questionAnswers
          .map(ans => {
            const option = HEALTH_QUESTIONNAIRE_FIELDS.FAMILY_HISTORY.diseaseGroups
              .flatMap(group => group.diseaseOptions)
              .find(opt => opt.choiceId === ans.choiceId);
            return option ? this.familyHistoryDiseasePipe.transform(option.name) : '';
          })
          .filter(name => name !== '');
      }
    }

    // 恢復自覺症狀
    const tempSubjectiveCheck = this.sharedStorageService.get(STORAGE_KEY.TEMP_SUBJECTIVE_SYMPTOM_DISEASE_CHECK);
    if (tempSubjectiveCheck) {
      this.subjectiveSymptomDiseaseCheck = tempSubjectiveCheck;
      
      // 檢查是否有"以下皆無"
      const hasNoneOfFollowing = this.subjectiveSymptomDiseaseCheck.questionAnswers.some(ans => ans.choiceId === '1510');
      if (hasNoneOfFollowing) {
        this.subjectiveSymptomNoneOfFollowing = true;
        const value = this.subjectiveSymptomDiseasePipe.transform(NONE_OF_FOLLOWING);
        this.subjectiveSymptomDiseases = [value];
      } else {
        // 恢復其他選項
        this.subjectiveSymptomDiseases = this.subjectiveSymptomDiseaseCheck.questionAnswers
          .map(ans => {
            const option = HEALTH_QUESTIONNAIRE_FIELDS.SUBJECTIVE_SYMPTOMS.diseaseGroups
              .flatMap(group => group.diseaseOptions)
              .find(opt => opt.choiceId === ans.choiceId);
            return option ? this.subjectiveSymptomDiseasePipe.transform(option.name) : '';
          })
          .filter(name => name !== '');
      }
    }

    console.log('強制恢復後的資料:', {
      personalDiseases: this.personalDiseases,
      familyDiseases: this.familyDiseases,
      subjectiveSymptomDiseases: this.subjectiveSymptomDiseases
    });
  }

  // 更新 URL 以反映當前步驟
  private updateUrlByStep(step: number): void {
    const pathMap: Record<number, string> = {
      1: '/healthcheck/reserve/personalhistory',
      2: '/healthcheck/reserve/familyhistory',
      3: '/healthcheck/reserve/symptoms',
    };
    if (pathMap[step]) {
      this.location.replaceState(pathMap[step]);
    }
  }
}
